import { useCallback, useEffect, useRef, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { FaHistory } from "react-icons/fa";
import http from "../../api/apiClient";
import Spinner from "../../components/Spinner";
import { LdsOverviewHero, LdsOverviewPage } from "../../components/lds/LdsOverview";
import {
  LdsTable,
  LdsTableBody,
  LdsTableCard,
  LdsTableCell,
  LdsTableHead,
  LdsTableHeadCell,
  LdsTableRow,
  LdsTableScroll,
} from "../../components/lds/LdsTable";
import { formatDutchDate } from "../../utils/dutchDate";
import { parseToDate } from "../../utils/dateUtils";

/** One fetch from e-Boekhouden; counts are empty when it failed. */
export type ImportRun = {
  id: string;
  at: unknown;
  by?: string | null;
  ok: boolean;
  status?: string | null;
  from?: string | null;
  to?: string | null;
  found?: number | null;
  imported?: number | null;
  alreadyImported?: number | null;
  linkedByIban?: number | null;
  linkedByRelation?: number | null;
  waiting?: number | null;
  skipped?: number | null;
  /** Payments waiting from earlier fetches that this fetch could link by IBAN. */
  linkedLater?: number | null;
  /** Started by the schedule, not by a person. */
  automatic?: boolean;
  /** Why it failed, on one line. */
  detail?: string | null;
};

type ImportLog = { runs: ImportRun[]; page: number; hasMore: boolean };

type TFn = ReturnType<typeof useTranslation>["t"];

/** The backend may send a moment as epoch seconds, a [y, m, d, h, min, s] list or ISO text. */
export const runMoment = (value: unknown): Date | null => {
  if (typeof value === "number") return new Date(value < 1e11 ? value * 1000 : value);
  return parseToDate(value);
};

const momentText = (value: unknown): string => {
  const date = runMoment(value);
  if (!date) return "—";
  const time = `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
  return `${formatDutchDate(date)} ${time}`;
};

const failureText = (t: TFn, status?: string | null): string => {
  switch (status) {
    case "NOT_CONFIGURED": return t("finance.import_log.failure.not_configured", { defaultValue: "Not set up" });
    case "DISABLED": return t("finance.import_log.failure.disabled", { defaultValue: "Connection switched off" });
    case "UNREACHABLE": return t("finance.import_log.failure.unreachable", { defaultValue: "e-Boekhouden not reachable" });
    case "REJECTED": return t("finance.import_log.failure.rejected", { defaultValue: "Refused by e-Boekhouden (token?)" });
    default: return t("finance.import_log.failure.error", { defaultValue: "Unexpected error" });
  }
};

const count = (value?: number | null): string => (value === null || value === undefined ? "—" : String(value));

/** Log of the e-Boekhouden connection: every fetch, whether it worked and how many payments it held. */
export default function FinanceImportLog(): ReactElement {
  const { t } = useTranslation();
  const [runs, setRuns] = useState<ImportRun[] | null>(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [failed, setFailed] = useState(false);
  const latest = useRef(0);

  const load = useCallback((nextPage: number) => {
    const request = ++latest.current;
    if (nextPage > 0) setLoadingMore(true);
    http.get("/finance/payments/imports", { params: { page: nextPage, size: 25 } })
      .then(({ data }: { data: ImportLog }) => {
        if (request !== latest.current) return;
        const received = Array.isArray(data?.runs) ? data.runs : [];
        setRuns((current) => (nextPage === 0 || !current ? received : [...current, ...received]));
        setPage(nextPage);
        setHasMore(Boolean(data?.hasMore));
        setFailed(false);
      })
      .catch(() => {
        if (request !== latest.current) return;
        setFailed(true);
        setRuns((current) => current ?? []);
      })
      .finally(() => {
        if (request === latest.current) setLoadingMore(false);
      });
  }, []);

  useEffect(() => {
    load(0);
  }, [load]);

  const head = "!px-3 whitespace-nowrap";
  const number = "!px-3 !py-2 text-right tabular-nums";
  return (
    <LdsOverviewPage>
      <LdsOverviewHero
        icon={FaHistory}
        eyebrow={t("finance.overview.title", { defaultValue: "Finance" })}
        title={t("finance.import_log.title", { defaultValue: "Connection log" })}
        description={t("finance.import_log.description", {
          defaultValue: "Every fetch from e-Boekhouden: when it ran, whether it worked and how many payments it contained.",
        })}
      />
      <LdsTableCard>
        {failed && (
          <p role="alert" className="px-4 py-3 text-sm font-semibold text-rose-600">
            {t("finance.import_log.load_failed", { defaultValue: "The log could not be loaded. Please try again." })}
          </p>
        )}
        {runs === null ? (
          <div className="flex justify-center py-8"><Spinner /></div>
        ) : (
          <LdsTableScroll>
            <LdsTable className="w-full text-xs">
              <LdsTableHead>
                <LdsTableRow>
                  <LdsTableHeadCell className={head}>{t("finance.import_log.columns.moment", { defaultValue: "When" })}</LdsTableHeadCell>
                  <LdsTableHeadCell className={head}>{t("finance.import_log.columns.result", { defaultValue: "Result" })}</LdsTableHeadCell>
                  <LdsTableHeadCell className={head}>{t("finance.import_log.columns.by", { defaultValue: "By" })}</LdsTableHeadCell>
                  <LdsTableHeadCell className={head}>{t("finance.import_log.columns.period", { defaultValue: "Period" })}</LdsTableHeadCell>
                  <LdsTableHeadCell className={`${head} text-right`}>{t("finance.import_log.columns.found", { defaultValue: "Found" })}</LdsTableHeadCell>
                  <LdsTableHeadCell className={`${head} text-right`}>{t("finance.import_log.columns.imported", { defaultValue: "New" })}</LdsTableHeadCell>
                  <LdsTableHeadCell className={`${head} text-right`}>{t("finance.import_log.columns.already", { defaultValue: "Already known" })}</LdsTableHeadCell>
                  <LdsTableHeadCell className={`${head} text-right`}>{t("finance.import_log.columns.linked", { defaultValue: "Linked" })}</LdsTableHeadCell>
                  <LdsTableHeadCell className={`${head} text-right`}>{t("finance.import_log.columns.waiting", { defaultValue: "To link" })}</LdsTableHeadCell>
                  <LdsTableHeadCell className={`${head} text-right`}>{t("finance.import_log.columns.skipped", { defaultValue: "Skipped" })}</LdsTableHeadCell>
                </LdsTableRow>
              </LdsTableHead>
              <LdsTableBody>
                {runs.length === 0 ? (
                  <LdsTableRow>
                    <LdsTableCell colSpan={10} className="py-8 text-center text-slate-500">
                      {t("finance.import_log.empty", { defaultValue: "Nothing has been fetched from e-Boekhouden yet." })}
                    </LdsTableCell>
                  </LdsTableRow>
                ) : runs.map((run) => {
                  const linked = run.ok ? (run.linkedByIban ?? 0) + (run.linkedByRelation ?? 0) + (run.linkedLater ?? 0) : null;
                  return (
                    <LdsTableRow key={run.id} data-run={run.id}>
                      <LdsTableCell className="!px-3 !py-2 whitespace-nowrap tabular-nums">{momentText(run.at)}</LdsTableCell>
                      <LdsTableCell className="!px-3 !py-2">
                        {run.ok ? (
                          <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-400/10 dark:text-emerald-300 dark:ring-emerald-400/30">
                            {t("finance.import_log.ok", { defaultValue: "Succeeded" })}
                          </span>
                        ) : (
                          <span className="inline-flex flex-wrap items-center gap-1.5">
                            <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-semibold text-rose-700 ring-1 ring-rose-200 dark:bg-rose-400/10 dark:text-rose-300 dark:ring-rose-400/30">
                              {t("finance.import_log.failed", { defaultValue: "Failed" })}
                            </span>
                            <span className="text-[11px] text-slate-500">{failureText(t, run.status)}</span>
                            {run.detail && (
                              <span className="block w-full max-w-[22rem] break-words font-mono text-[11px] text-rose-700 dark:text-rose-300" title={run.detail}>
                                {run.detail}
                              </span>
                            )}
                          </span>
                        )}
                      </LdsTableCell>
                      <LdsTableCell className="!px-3 !py-2">{run.automatic ? t("finance.import_log.automatic", { defaultValue: "Automatic" }) : run.by || "—"}</LdsTableCell>
                      <LdsTableCell className="!px-3 !py-2 whitespace-nowrap tabular-nums">
                        {run.from && run.to ? `${formatDutchDate(run.from)} – ${formatDutchDate(run.to)}` : "—"}
                      </LdsTableCell>
                      <LdsTableCell className={number}>{count(run.found)}</LdsTableCell>
                      <LdsTableCell className={number}>{count(run.imported)}</LdsTableCell>
                      <LdsTableCell className={number}>{count(run.alreadyImported)}</LdsTableCell>
                      <LdsTableCell className={number} title={run.ok
                        ? t("finance.import_log.linked_detail", {
                          iban: run.linkedByIban ?? 0, relation: run.linkedByRelation ?? 0, later: run.linkedLater ?? 0,
                          defaultValue: "{{iban}} by IBAN, {{relation}} by remembered payer, {{later}} that were waiting",
                        })
                        : undefined}>
                        {count(linked)}
                      </LdsTableCell>
                      <LdsTableCell className={number}>{count(run.waiting)}</LdsTableCell>
                      <LdsTableCell className={number}>{count(run.skipped)}</LdsTableCell>
                    </LdsTableRow>
                  );
                })}
              </LdsTableBody>
            </LdsTable>
          </LdsTableScroll>
        )}
        {hasMore && (
          <div className="flex justify-center p-3">
            <button
              type="button"
              onClick={() => load(page + 1)}
              disabled={loadingMore}
              className="inline-flex h-9 items-center rounded-lg border border-slate-200 px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40 dark:border-white/10 dark:text-slate-200 dark:hover:bg-white/5"
            >
              {t("finance.import_log.more", { defaultValue: "Show older fetches" })}
            </button>
          </div>
        )}
      </LdsTableCard>
    </LdsOverviewPage>
  );
}
